import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "@store/index";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";
import {
  ArticleCreateProps,
  BillArticleProps,
  BillArticleResponseProps,
  BillCreateProps,
  BillDraftProps,
  BillItemProps,
  BillItemReadOnly,
  BillProps,
  BillResponseProps,
  CustomCreateProps,
  CustomsProps,
  Notes,
  PackingProps,
  PackingPropsResponse,
  ProtocolsListProps,
  ProtocolsListResponse,
  ProtocolsUpdateProps,
  RadiometricsProps,
  RadiometricsResponse,
  RadiometricsUpdateProps,
  AggregatedPackingPropsResponse,
  ArticleError,
} from "./types";
import {
  createBillArticlesApi,
  createBillGeneralApi,
  createCustomsGeneralApi,
  exportCustomsBillApi,
  getBillArticlesApi,
  getBillDetailsApi,
  getBillDraftApi,
  getBillGeneralApi,
  getBillListApi,
  getBulkRadiometricsApi,
  getContainerRadiometricsApi,
  getControlProtocolsApi,
  getCustomsGeneralApi,
  getFileUrlApi,
  getPackingShortApi,
  getAggregatedPackingShortApi,
  removeArticleApi,
  updateArticleApi,
  updateBillGeneralApi,
  updateCustomsGeneralApi,
  updateCustomsNotesApi,
  updatePackingListApi,
  updateProtocolsApi,
  updateRadiometricsApi,
  cancelCustomsBillApi,
  fetchPackingListApi,
} from "./api";

type ViewState<T> = {
  items: T;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type ViewStateWithArticleError<T> = {
  items: T;
  loading: boolean;
  error: ArticleError | null;
  success: string | null;
};

type CustomsState = {
  general: ViewState<CustomsProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  radiometrics: ViewState<RadiometricsProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  protocols: ViewState<ProtocolsListProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  bill: ViewState<BillProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  billArticles: ViewStateWithArticleError<BillArticleProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  packing: ViewState<PackingProps[] | null> & {
    total: number;
    page: number;
    size: number;
  };
  packing_list: ViewState<PackingProps[] | null> & {
    total: number;
    page: number;
    size: number;
    entity_id?: number | null;
  };
  aggregated: ViewState<AggregatedPackingPropsResponse | null>;
  billDetails: ViewState<BillItemProps | null>;
  notes: ViewState<Notes | null>;
  activeBill: { id: number; ref: string; bill_draft: string } | null;
  draft: ViewState<BillDraftProps[] | null>;
  readOnlyBill: ViewState<BillItemReadOnly | null>;
  exporting: {
    loading: boolean;
    error: string | null;
    id: number | null;
  };
};

export type CustomsKey =
  | "general"
  | "radiometrics"
  | "protocols"
  | "notes"
  | "bill"
  | "billDetails"
  | "packing"
  | "aggregated"
  | "draft"
  | "billArticles"
  | "packing_list"
  | "readOnlyBill";

export type CustomsKeyWithoutArticles = Exclude<CustomsKey, "billArticles">;

const getInitialState = <T>(items: T): ViewState<T> => ({
  items,
  loading: false,
  error: null,
  success: null,
});

const getInitialStateWithArticleError = <T>(
  items: T,
): ViewStateWithArticleError<T> => ({
  items,
  loading: false,
  error: null,
  success: null,
});

const initialState: CustomsState = {
  general: {
    ...getInitialState<CustomsProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  radiometrics: {
    ...getInitialState<RadiometricsProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  protocols: {
    ...getInitialState<ProtocolsListProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  bill: {
    ...getInitialState<BillProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  packing: {
    ...getInitialState<PackingProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  packing_list: {
    ...getInitialState<PackingProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  billArticles: {
    ...getInitialStateWithArticleError<BillArticleProps[] | null>(null),
    total: 0,
    page: 0,
    size: 0,
  },
  aggregated: getInitialState<AggregatedPackingPropsResponse | null>(null),
  billDetails: {
    ...getInitialState<BillItemProps | null>(null),
  },
  notes: {
    ...getInitialState<Notes | null>(null),
  },
  draft: {
    ...getInitialState<BillDraftProps[] | null>(null),
  },
  activeBill: null,
  exporting: {
    loading: false,
    error: null,
    id: null,
  },
  readOnlyBill: {
    ...getInitialState<BillItemReadOnly | null>(null),
  },
};

const customsSlice = createSlice({
  name: "customs",
  initialState,
  reducers: {
    getStart(state: CustomsState, action: PayloadAction<CustomsKey>) {
      state[action.payload].loading = true;
      state[action.payload].error = null;
    },
    getSuccess(
      state: CustomsState,
      action: PayloadAction<{
        key: CustomsKey;
        items?: any;
        total?: number;
        entity_id?: number;
      }>,
    ) {
      const { key, items, total } = action.payload;
      if (items) state[key].items = items;
      if (total !== undefined && "total" in state[key])
        state[key].total = total;
      state[key].loading = false;
      state[key].error = null;
      if (action.payload.entity_id !== undefined && key === "packing_list") {
        state[key].entity_id = action.payload.entity_id;
      }
    },
    getProtocolsSuccess(
      state: CustomsState,
      action: PayloadAction<ProtocolsListResponse>,
    ) {
      const { items, total, page, size } = action.payload;
      state.protocols.items = items;
      state.protocols.total = total;
      state.protocols.page = page;
      state.protocols.size = size;
      state.protocols.loading = false;
    },
    getRadiometricsSuccess(
      state: CustomsState,
      action: PayloadAction<RadiometricsResponse>,
    ) {
      const { items, total, page, size } = action.payload;
      state.radiometrics.items = items;
      state.radiometrics.total = total;
      state.radiometrics.page = page;
      state.radiometrics.size = size;
      state.radiometrics.loading = false;
    },
    createSuccess(
      state: CustomsState,
      action: PayloadAction<{ key: CustomsKey; success: string }>,
    ) {
      const { key, success } = action.payload;
      state[key].success = success;
      state[key].loading = false;
      state[key].error = null;
    },
    getFailure(
      state: CustomsState,
      action: PayloadAction<{ key: CustomsKeyWithoutArticles; error: string }>,
    ) {
      const { key, error } = action.payload;
      if (key === "readOnlyBill") {
        state.readOnlyBill.items = null;
      }
      state[key].loading = false;
      state[key].error = error;
      state.exporting.loading = false;
    },
    cleanData(state: CustomsState, action: PayloadAction<CustomsKey>) {
      state[action.payload].items = null;
      state[action.payload].success = null;
      state[action.payload].error = null;
      if (action.payload === "packing_list") {
        state[action.payload].total = 0;
        state[action.payload].entity_id = null;
      }
    },
    hideWarning(
      state: CustomsState,
      action: PayloadAction<CustomsKey | CustomsKey[]>,
    ) {
      const keys = Array.isArray(action.payload)
        ? action.payload
        : [action.payload];

      keys.forEach((key) => {
        if (!state[key]) return;
        state[key].error = null;
        state[key].success = null;
      });
    },
    setRadioMetricsList(
      state: CustomsState,
      action: PayloadAction<{ id: number; data: RadiometricsUpdateProps }>,
    ) {
      const { data } = action.payload;
      if (!state.radiometrics.items) return;

      const idx = state.radiometrics.items.findIndex((item) =>
        item?.bl_container_id
          ? item.bl_container_id === data.bl_container_id
          : item.bl_id === data.bl_id,
      );
      if (idx !== -1) {
        state.radiometrics.items[idx].radiometric_check = data;
      }
    },
    setProtocolsList(
      state: CustomsState,
      action: PayloadAction<{ id: number; data: ProtocolsListProps }>,
    ) {
      const { data } = action.payload;
      if (!state.protocols.items) return;

      const idx = state.protocols.items.findIndex(
        (item) => item.id === data.id,
      );
      if (idx !== -1) {
        state.protocols.items[idx] = data;
      }
    },
    getBillListSuccess(
      state: CustomsState,
      action: PayloadAction<BillResponseProps>,
    ) {
      const { items, total, page, size } = action.payload;
      state.bill.items = items;
      state.bill.total = total;
      state.bill.page = page;
      state.bill.size = size;
      state.bill.loading = false;
    },
    getPackingSuccess(
      state: CustomsState,
      action: PayloadAction<PackingPropsResponse>,
    ) {
      const { items, total, page, size } = action.payload;
      state.packing.items = items;
      state.packing.total = total;
      state.packing.page = page;
      state.packing.size = size;
      state.packing.loading = false;
    },
    getAggregatedSuccess(
      state: CustomsState,
      action: PayloadAction<AggregatedPackingPropsResponse>,
    ) {
      state.aggregated.items = action.payload;
      state.aggregated.loading = false;
      state.aggregated.error = null;
    },
    setCustomsBill(
      state: CustomsState,
      action: PayloadAction<{ data: BillItemProps; id?: number }>,
    ) {
      const { data } = action.payload;
      const existingArticles = state.billDetails.items?.articles ?? [];

      state.billDetails.items = {
        ...state.billDetails.items,
        ...data,
        articles: existingArticles,
      };
      state.billDetails.loading = false;
    },
    setBillArticles(
      state: CustomsState,
      action: PayloadAction<{
        data: BillArticleProps | BillArticleResponseProps;
        id: number;
      }>,
    ) {
      const { data, id } = action.payload;
      const newItems = "items" in data ? data.items : [data];
      const item = state.billDetails.items;

      if (!item || item.id !== id) {
        state.billDetails.loading = false;
        return;
      }

      const articlesWithoutDuplicate = (item.articles || []).filter(
        (a) => a.id !== undefined,
      );

      const existingIds = new Set(articlesWithoutDuplicate.map((a) => a.id));
      const uniqueItems = newItems.filter((a) => !existingIds.has(a.id));

      item.articles = [...articlesWithoutDuplicate, ...uniqueItems];
      state.billDetails.loading = false;
    },
    updateBillArticles(
      state: CustomsState,
      action: PayloadAction<{ id: number; data: BillArticleProps }>,
    ) {
      const { data, id } = action.payload;
      const item = state.billDetails.items;

      if (!item || !Array.isArray(item.articles)) return;

      const index = item.articles.findIndex((el) => el.id === id);
      if (index === -1) return;

      item.articles[index] = { ...item.articles[index], ...data };
    },
    removeArticle(state: CustomsState, action: PayloadAction<number>) {
      const item = state.billDetails.items;

      if (!item || !Array.isArray(item.articles)) return;

      item.articles = item.articles.filter((el) => el.id !== action.payload);
    },
    setDuplicateArticle(state: CustomsState, action: PayloadAction<number>) {
      const id = action.payload;
      const bill = state.billDetails.items;
      if (!bill || !Array.isArray(bill.articles)) return;

      const article = bill.articles.find((item) => item.id === id);
      if (!article) return;

      const {
        id: _,
        customs_bill_id,
        hs_code,
        caad_code,
        ...articleWithoutId
      } = article;

      bill.articles = [
        ...bill.articles,
        { ...articleWithoutId, id: undefined },
      ];
    },
    removeDuplicateArticle(state: CustomsState) {
      const bill = state.billDetails.items;
      if (!bill || !Array.isArray(bill.articles)) return;

      bill.articles = bill.articles.filter((item) => item.id !== undefined);
    },
    startExport(state: CustomsState, action: PayloadAction<number>) {
      state.exporting = {
        id: action.payload,
        loading: true,
        error: null,
      };
    },
    successExport(state: CustomsState) {
      state.exporting.loading = false;
      state.exporting.id = null;
      state.exporting.error = null;
    },
    getArticleError(state: CustomsState, action: PayloadAction<ArticleError>) {
      state.billArticles.error = action.payload;
      state.billArticles.loading = false;
    },
    clearArticleFieldError(state: CustomsState, action: PayloadAction<string>) {
      const fieldName = action.payload;
      if (state.billArticles.error?.meta?.fields) {
        state.billArticles.error.meta.fields =
          state.billArticles.error.meta.fields.filter(
            (f) => f.field !== fieldName,
          );
        if (state.billArticles.error.meta.fields.length === 0) {
          state.billArticles.error = null;
        }
      }
    },
  },
});

export const {
  getStart,
  cleanData,
  getSuccess,
  getFailure,
  hideWarning,
  startExport,
  successExport,
  removeArticle,
  createSuccess,
  setCustomsBill,
  getArticleError,
  clearArticleFieldError,
  setBillArticles,
  getPackingSuccess,
  getAggregatedSuccess,
  updateBillArticles,
  getBillListSuccess,
  setRadioMetricsList,
  getProtocolsSuccess,
  setProtocolsList,
  setDuplicateArticle,
  removeDuplicateArticle,
} = customsSlice.actions;

export const { actions, reducer } = customsSlice;
export default customsSlice.reducer;

export const fetchCustomsGeneral =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("general"));
      const data = await getCustomsGeneralApi(id);
      dispatch(getSuccess({ key: "general", items: data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "general", error }));
    }
  };

export const createCustomsGeneral =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<CustomCreateProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      await createCustomsGeneralApi(id, params);
      dispatch(fetchCustomsGeneral(id));
      dispatch(
        createSuccess({
          key: "general",
          success: "Customs information created successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "general", error }));
    }
  };

export const updateCustomsGeneral =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<CustomCreateProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      await updateCustomsGeneralApi(id, params);
      dispatch(fetchCustomsGeneral(id));
      dispatch(
        createSuccess({
          key: "general",
          success: "Customs information updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "general", error }));
    }
  };

export const fetchRadiometrics =
  (id: number, type: string, params: any): AppThunk =>
  async (dispatch) => {
    try {
      let data;
      dispatch(getStart("radiometrics"));
      if (type === "BULK") {
        data = await getBulkRadiometricsApi(id, params);
      }
      if (type === "CONTAINER") {
        data = await getContainerRadiometricsApi(id, params);
      }
      dispatch(
        getSuccess({
          key: "radiometrics",
          items: data?.items,
          total: data?.total,
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "radiometrics", error }));
    }
  };

export const updateRadiometrics =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<RadiometricsProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("radiometrics"));
      const data = await updateRadiometricsApi(id, params);
      dispatch(setRadioMetricsList({ id, data }));
      dispatch(
        createSuccess({
          key: "radiometrics",
          success: "Radiometric check updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "radiometrics", error }));
    }
  };

export const updateCustomsNotes =
  ({
    id,
    params,
  }: {
    id: number;
    params: {
      radiometric_check_instruction: string;
      radiometric_check_note: string;
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      await updateCustomsNotesApi(id, params);
      dispatch(getSuccess({ key: "notes", items: params }));
      dispatch(
        createSuccess({
          key: "radiometrics",
          success: "Radiometric check notes updated successfully",
        }),
      );
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "radiometrics", error }));
    }
  };

export const fetchBillList =
  (id: number, params?: any): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("bill"));
      const data = await getBillListApi(id, params);
      dispatch(getBillListSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "bill", error }));
    }
  };

export const cancelCustomsBill =
  (id: number, sid: number, params?: any): AppThunk =>
  async (dispatch) => {
    try {
      await cancelCustomsBillApi(id);
      dispatch(fetchBillList(sid, params));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "bill", error }));
    }
  };

export const createBillGeneral =
  ({
    id,
    params,
    oid,
    navigate,
  }: {
    id: number;
    params: Partial<BillCreateProps>;
    oid: number;
    navigate: any;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await createBillGeneralApi(id, params);
      dispatch(fetchBillDetails(id, data.id));
      dispatch(
        createSuccess({
          key: "billDetails",
          success: "Customs bill created successfully",
        }),
      );
      navigate(`/offers/${oid}/shipping-case/${id}/customs/bills/${data.id}`);
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billDetails", error }));
    }
  };

export const fetchBillDetails =
  (scId: number, id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("billDetails"));
      const data = await getBillDetailsApi(id);
      if (!["A22", "A28"].includes(data.bill_type)) {
        dispatch(fetchBillGeneral(scId, data.bill_draft, data.bill_type));
        dispatch(fetchBillArticles(scId, id));
      }

      dispatch(setCustomsBill({ data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billDetails", error }));
    }
  };

export const fetchBillGeneral =
  (id: number, bill_draft: string, bill_type: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("readOnlyBill"));
      const { bill, articles, missing_hs_codes, dpo_number } =
        await getBillGeneralApi(id, bill_draft, bill_type);
      dispatch(
        getSuccess({
          key: "readOnlyBill",
          items: { bill, articles, missing_hs_codes, dpo_number },
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "readOnlyBill", error }));
    }
  };

export const fetchBillArticles =
  (scId: number, id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("billDetails"));
      const data = await getBillArticlesApi(scId, id);
      dispatch(setBillArticles({ data, id }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billDetails", error }));
    }
  };

export const createBillArticles =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<ArticleCreateProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await createBillArticlesApi(id, params);
      dispatch(setBillArticles({ data, id }));
      dispatch(
        createSuccess({
          key: "billDetails",
          success: "Customs bill article added successfully",
        }),
      );
    } catch (err) {
      if ((err as any)?.response?.status === 422) {
        const error = (err as any)?.response?.data;
        dispatch(getArticleError(error as ArticleError));
      } else {
        const error = getErrorMessageFromResponse(err);
        dispatch(getFailure({ key: "billDetails", error }));
      }
      throw err;
    }
  };

export const updateBillGeneral =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<BillCreateProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateBillGeneralApi(id, params);
      dispatch(setCustomsBill({ data }));
      dispatch(
        createSuccess({
          key: "billDetails",
          success: "Customs bill updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billDetails", error }));
    }
  };

export const updateCustomsArticle =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<ArticleCreateProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const data = await updateArticleApi(id, params);
      dispatch(updateBillArticles({ data, id }));
      dispatch(
        createSuccess({
          key: "billDetails",
          success: "Customs article updated successfully",
        }),
      );
    } catch (err) {
      if ((err as any)?.response?.status === 422) {
        const error = (err as any)?.response?.data;
        dispatch(getArticleError(error as ArticleError));
      } else {
        const error = getErrorMessageFromResponse(err);
        dispatch(getFailure({ key: "billDetails", error }));
      }
      throw err;
    }
  };

export const deleteArticleDetails =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeArticleApi(id);
      dispatch(removeArticle(id));
      dispatch(
        createSuccess({
          key: "billDetails",
          success: "Customs article removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billDetails", error }));
    }
  };

export const fetchPacking =
  ({
    id,
    params,
  }: {
    id: number;
    params: {
      page: number;
      size: number;
      customs_bill_draft?: string;
      intro_customs_bill_draft?: string;
      order_by?: string;
      is_locked_for_fcr?: boolean;
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("packing"));
      const data = await getPackingShortApi(id, params);
      dispatch(getPackingSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "packing", error }));
    }
  };

export const fetchAggregatedPacking =
  ({ id, params }: { id: number; params: Record<string, any> }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("aggregated"));
      const data = await getAggregatedPackingShortApi(id, params);
      dispatch(getAggregatedSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "aggregated", error }));
    }
  };

export const fetchBillDraft =
  (id: number, params: { customs_bill_type: string }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("draft"));
      const data = await getBillDraftApi(id, params);
      dispatch(getSuccess({ key: "draft", items: data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billDetails", error }));
    }
  };

export const updatePackingList =
  ({
    id,
    scId,
    params,
  }: {
    id: number;
    scId: number;
    params: {
      mrn_code: string;
      packing_list_ids: number[];
    };
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("packing"));
      await updatePackingListApi(id, params);
      dispatch(fetchBillDetails(scId, id));
      dispatch(
        createSuccess({
          key: "billDetails",
          success: "Bill was cleared successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billDetails", error }));
    }
  };

export const exportCustomsBillListing =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport(id));
      const { file, filename } = await exportCustomsBillApi(id);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch {
      dispatch(
        getFailure({
          key: "billDetails",
          error: "Downloading failed. Try again later.",
        }),
      );
    }
  };

export const getControlProtocols =
  (params?: { page: number; size: number }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("protocols"));
      const data = await getControlProtocolsApi(params);
      dispatch(getProtocolsSuccess(data));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "protocols", error }));
    }
  };

export const updateProtocols =
  ({
    id,
    params,
  }: {
    id: number;
    params: Partial<ProtocolsUpdateProps>;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("protocols"));
      const data = await updateProtocolsApi(id, params);
      dispatch(setProtocolsList({ id, data }));
      dispatch(
        createSuccess({
          key: "protocols",
          success: "Control protocol updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "protocols", error }));
    }
  };

export const getProtocolsDocument =
  (id: number, name: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport(id));
      const { presigned_url } = await getFileUrlApi(id);
      if (presigned_url) {
        const response = await fetch(presigned_url);
        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `${name}.pdf`;
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
      }

      dispatch(successExport());
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      dispatch(getFailure({ key: "protocols", error }));
    }
  };

export const fetchPackingList =
  (params: {
    entity_id: number;
    entity: string;
    page: number;
    size: number;
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("packing_list"));
      const data = await fetchPackingListApi(params);
      dispatch(
        getSuccess({
          key: "packing_list",
          entity_id: params.entity_id,
          items: data.items,
          total: data.total,
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "packing_list", error }));
    }
  };
